/**
 * Community module — mock widget loaders.
 *
 * Lists are sent in the order each value is first met in the rows — the
 * prototype's `Map` insertion order, which its `pivot` and hand-rolled tallies
 * then stable-sorted. The presenters do that sort, so a tie resolves the way it
 * did there.
 */
import type { DashboardQuery, SeriesKey, WidgetLoaders } from '@/data-sources/contract';
import type { AudienceSegment, RosterValue } from '@/data-sources/contract/widgets/community';

import { aggregate } from '../engine/aggregate';
import { byVoice, complaints, inCommunity } from '../engine/cuts';
import { dimensionIndex, type DimensionKey } from '../engine/dimensions';
import {
  AGE_BANDS,
  COMMUNITY_LABELS,
  COMMUNITY_MEMBERS,
  COMMUNITY_POST_CONVERSATIONS,
  COMMUNITY_VALUES,
  DIVISION_LABELS,
  DIVISION_VALUES,
  GENDER_LABELS,
  GENDER_VALUES,
  VOICE_LABELS,
  VOICE_VALUES,
  type MentionRow,
} from '../engine/generator';
import { weekHourGrid } from '../engine/grid';
import { metrics } from '../engine/metrics';
import { scoped } from '../query';
import { comparisonOf, timeSeriesOf } from './shared';

/* ------------------------------------------------------------ helpers */

/** Rows grouped by a field, groups in first-met order (the prototype's `Map` insertion order). */
function groupBy(
  rows: readonly MentionRow[],
  read: (row: MentionRow) => string | null,
): Map<string, MentionRow[]> {
  const groups = new Map<string, MentionRow[]>();
  for (const row of rows) {
    const key = read(row);
    if (key === null) continue;
    const group = groups.get(key);
    if (group) group.push(row);
    else groups.set(key, [row]);
  }
  return groups;
}

function roster(
  values: readonly string[],
  labels: Readonly<Record<string, string>>,
  key: string,
): RosterValue {
  const index = values.indexOf(key);
  return { key, label: labels[key] ?? key, index: index < 0 ? 0 : index };
}

/** A dimension's values present in the rows, in option (fixture) order. */
function present(rows: readonly MentionRow[], dimension: DimensionKey): SeriesKey[] {
  const seen = new Set<string>();
  for (const row of rows) seen.add(row[dimension]);
  const order = dimensionIndex[dimension];
  return [...seen]
    .sort((a, b) => (order[a] ?? 0) - (order[b] ?? 0))
    .map((key) => ({ key, dimension }));
}

const isPost = (row: MentionRow) => COMMUNITY_POST_CONVERSATIONS.includes(row.conversation);

/**
 * The activity grid's rows: the user's brand over the range, sentiment-corrected
 * and chip-filtered. Exported for the drill resolver.
 */
export function activityRows(query: DashboardQuery): readonly MentionRow[] {
  return scoped(query).own;
}

/* ------------------------------------------------------------ loaders */

export const communityWidgetLoaders = {
  'activity-by-day-and-hour': async ({ query }) => ({
    engagement: weekHourGrid(activityRows(query), 'engagement').values,
  }),

  'top-communities-by-mention': async ({ query }) => {
    const groups = groupBy(inCommunity(scoped(query).own), (row) => row.community);
    const communities = [...groups.entries()].map(([key, group]) => ({
      ...roster(COMMUNITY_VALUES, COMMUNITY_LABELS, key),
      mentions: group.length,
      members: COMMUNITY_MEMBERS[key] ?? 0,
    }));
    return { communities };
  },

  'top-communities-by-sentiment': async ({ query }) => {
    const communityRows = inCommunity(scoped(query).own);
    const sentiments = present(communityRows, 'sentiment');
    const groups = groupBy(communityRows, (row) => row.community);
    const communities = [...groups.entries()].map(([key, group]) => ({
      ...roster(COMMUNITY_VALUES, COMMUNITY_LABELS, key),
      values: sentiments.map((s) => group.filter((row) => row.sentiment === s.key).length),
    }));
    return { sentiments, communities };
  },

  'community-activity-timeline': async ({ query }) => {
    const s = scoped(query);
    const communityRows = inCommunity(s.own);
    const posts = communityRows.filter(isPost);
    const replies = communityRows.filter((row) => !isPost(row));
    const postWeeks = aggregate(posts, {
      metric: 'mentions',
      from: s.from,
      to: s.to,
      bucket: 'week',
    });
    const replyWeeks = aggregate(replies, {
      metric: 'mentions',
      from: s.from,
      to: s.to,
      bucket: 'week',
    });
    const previous = s.ownPrevious ? inCommunity(s.ownPrevious) : null;
    const spec = { metric: 'mentions' } as const;
    const postSeries = timeSeriesOf(
      postWeeks,
      s.from,
      s.to,
      undefined,
      comparisonOf(previous && previous.filter(isPost), s.previousBounds, postWeeks, spec),
    );
    const replySeries = timeSeriesOf(
      replyWeeks,
      s.from,
      s.to,
      undefined,
      comparisonOf(
        previous && previous.filter((row) => !isPost(row)),
        s.previousBounds,
        replyWeeks,
        spec,
      ),
    );
    return {
      weekly: {
        bucket: postSeries.bucket,
        x: postSeries.x,
        series: [
          { key: 'posts', values: postSeries.series[0]?.values ?? [] },
          { key: 'comments', values: replySeries.series[0]?.values ?? [] },
        ],
        previous:
          postSeries.previous && replySeries.previous
            ? [
                { key: 'posts', values: postSeries.previous[0]?.values ?? [] },
                { key: 'comments', values: replySeries.previous[0]?.values ?? [] },
              ]
            : null,
      },
      posts: { current: posts.length, previous: previous ? previous.filter(isPost).length : null },
      comments: {
        current: replies.length,
        previous: previous ? previous.filter((row) => !isPost(row)).length : null,
      },
    };
  },

  'audience-age-and-gender': async ({ query }) => {
    const rows = scoped(query).own;
    const segments = (source: readonly MentionRow[]): AudienceSegment[] =>
      [...groupBy(source, (row) => `${row.gender}|${row.ageBand}`).values()].map((group) => ({
        gender: roster(GENDER_VALUES, GENDER_LABELS, group[0]!.gender),
        ageBand: roster(AGE_BANDS, {}, group[0]!.ageBand),
        mentions: group.length,
      }));
    return {
      audience: segments(rows),
      negative: segments(rows.filter((row) => row.sentiment === 'negative')),
    };
  },

  'top-individual-voices': async ({ query }) => {
    const voiceRows = byVoice(scoped(query).own);
    const sentiments = present(voiceRows, 'sentiment');
    const groups = groupBy(voiceRows, (row) => row.voice);
    const voices = [...groups.entries()].map(([key, group]) => {
      const bySentiment = sentiments.map((s) => group.filter((row) => row.sentiment === s.key));
      return {
        ...roster(VOICE_VALUES, VOICE_LABELS, key),
        mentions: bySentiment.map((part) => part.length),
      };
    });
    return { sentiments, voices };
  },

  'complaints-by-division': async ({ query }) => {
    const groups = groupBy(complaints(scoped(query).own), (row) => row.division);
    const divisions = [...groups.entries()].map(([key, group]) => ({
      ...roster(DIVISION_VALUES, DIVISION_LABELS, key),
      complaints: group.length,
    }));
    return { divisions };
  },

  'community-topics': async ({ query }) => {
    const communityRows = inCommunity(scoped(query).own);
    const channels = present(communityRows, 'channel');
    const groups = groupBy(communityRows, (row) => row.topic);
    const topics = [...groups.entries()].map(([key, group]) => ({
      key,
      dimension: 'topic' as const,
      values: channels.map((c) =>
        metrics.engagement.reduce(group.filter((row) => row.channel === c.key)),
      ),
    }));
    return { channels, topics };
  },
} satisfies Partial<WidgetLoaders>;
